import { formatDistanceToNow } from 'date-fns';
import { upperFirst } from 'lodash';
import pluralize from 'pluralize';

import { type Tasks } from '@air/api';
import { type Board, ClipSource, type Subscription, type Task } from '@air/api/types';
import { FileStatus } from '@air/feature-file-status/constants';
import { type FileTrackingItem } from '@air/feature-file-status/ui';
import { UploadStatus } from '@air/redux-uploader';
import { formatBytes, parseAirDateToISO } from '@air/utilities';

import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';
import { ImportItemIcon } from '~/components/Import/ImportItemIcon';
import { getImporterReliabilityData } from '~/components/ImportHistory/utils';
import { type LocationBoard } from '~/store/router/types';
import { type ImporterTask } from '~/store/tasks/types';

type ImporterType = Awaited<ReturnType<typeof Tasks.listTasks<string, 'Importer'>>>['items'][number];

export type ImporterTaskEnhanced = ImporterType & {
  metadata: ImporterTask['metadata'];
};

export type NormalizedImportItem = {
  id: string;
  status: Task['status'];
  source: ImporterTaskEnhanced['args']['source'];
  board?: Pick<Board, 'id' | 'title' | 'thumbnails'>;
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string;
  storageExceeded?: boolean;
  filesImportedCount: number;
  foldersImported: number;
  fileImportError: number;
  folderImportError: number;
  totalFilesFound: number;
  totalFoldersFound: number;
  importSizeEstimate?: number;
  importedSize: number;
  skippedAlreadyImported: number;
  skippedExcluded: number;
  skippedZeroByte: number;
  skippedUnsupported: number;
  logMessage?: string;
  canCancel: boolean;
};

export const normalizeRemoteImport = (item: ImporterTaskEnhanced): NormalizedImportItem => {
  const reliability = getImporterReliabilityData(item.data);

  return {
    id: item.id,
    status: item.status,
    source: item.args.source,
    board: item.metadata.board,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    storageExceeded: item.data?.storageExceeded,
    filesImportedCount: item.data?.filesImportedCount ?? 0,
    foldersImported: item.data?.foldersImported ?? 0,
    fileImportError: item.data?.fileImportError ?? 0,
    folderImportError: item.data?.folderImportError ?? 0,
    totalFilesFound: item.data?.totalFilesFound ?? 0,
    totalFoldersFound: item.data?.totalFoldersFound ?? 0,
    importSizeEstimate: item.data?.importSizeEstimate,
    importedSize: item.data?.importedSize ?? 0,
    skippedAlreadyImported: reliability.skippedAlreadyImported ?? 0,
    skippedExcluded: reliability.skippedExcluded ?? 0,
    skippedZeroByte: reliability.skippedZeroByte ?? 0,
    skippedUnsupported: reliability.skippedUnsupported ?? 0,
    logMessage: item.data?.log?.at(-1),
    canCancel: ['active', 'pending'].includes(item.status),
  };
};

const localToRemoteStatus = (status: ImporterTask['status']): Task['status'] => {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'in-progress':
      return 'active';
    case 'completed':
      return 'succeeded';
    case 'error':
      return 'failed';
  }
};

export const normalizeLocalImport = (item: ImporterTask): NormalizedImportItem => ({
  id: item.localTaskId,
  status: localToRemoteStatus(item.status),
  source: item.metadata.source,
  board: item.metadata.board,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  startedAt: item.startedAt,
  storageExceeded: item.metadata.storageExceeded,
  filesImportedCount: item.metadata.filesImportedCount ?? 0,
  foldersImported: item.metadata.foldersImported ?? 0,
  fileImportError: item.metadata.fileImportError ?? 0,
  folderImportError: item.metadata.folderImportError ?? 0,
  totalFilesFound: item.metadata.totalFilesFound ?? 0,
  totalFoldersFound: item.metadata.totalFoldersFound ?? 0,
  importSizeEstimate: item.metadata.importSizeEstimate,
  importedSize: item.metadata.importedSize ?? 0,
  skippedAlreadyImported: item.metadata.skippedAlreadyImported ?? 0,
  skippedExcluded: item.metadata.skippedExcluded ?? 0,
  skippedZeroByte: item.metadata.skippedZeroByte ?? 0,
  skippedUnsupported: item.metadata.skippedUnsupported ?? 0,
  logMessage: item.metadata.log?.at(-1),
  canCancel: item.status === 'in-progress',
});

export const DEFAULT_IMPORT_DESTINATION_BOARD_TITLE = 'All media';

/** Shared import session status (legacy file-status pane + ui2026 imports tray). */
export const getImportUploadStatus = (status: Task['status'], storageExceeded?: boolean): UploadStatus => {
  switch (status) {
    case 'pending':
      return UploadStatus.queued;
    case 'active':
      return storageExceeded ? UploadStatus.stopped : UploadStatus.uploading;
    case 'succeeded':
      return UploadStatus.completed;
    case 'failed':
    case 'exception':
      return UploadStatus.failed;
    case 'cancelled':
      return UploadStatus.aborted;
    default:
      return UploadStatus.failed;
  }
};

export const getImportDestinationBoard = (item: NormalizedImportItem): NonNullable<NormalizedImportItem['board']> => ({
  id: item.board?.id ?? '',
  thumbnails: item.board?.thumbnails ?? [],
  title: item.board?.title ?? DEFAULT_IMPORT_DESTINATION_BOARD_TITLE,
});

export const getImportListItemTitle = (item: NormalizedImportItem): string => {
  const board = getImportDestinationBoard(item);

  return `${upperFirst(sourceTypeToName(item.source))} to ${board.title}`;
};

export const getImportListStatusCopy = (item: NormalizedImportItem): string => {
  if (['failed', 'exception'].includes(item.status)) {
    return `Error: ${getImportErrorMessage(item) || 'Failed'}`;
  }

  return getSubtitle(item) ?? '';
};

export const sourceTypeToName = (source: ImporterTaskEnhanced['args']['source']) => {
  switch (source) {
    case ClipSource.dropbox:
      return 'Dropbox';
    case ClipSource.googleDrive:
      return 'Google Drive';
    case ClipSource.box:
      return 'Box';
    case ClipSource.brandfolder:
      return 'Brandfolder';
    case ClipSource.sharePoint:
      return 'SharePoint';
    case ClipSource.frameio:
      return 'Frame.io';
    case ClipSource.canto:
      return 'Canto';
    default:
      return source;
  }
};

const compactNumber = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});

const formatParts = (...parts: string[]) => parts.join(' · ');

const SHOW_PROGRESS_ITEMS_REMAINING_THRESHOLD = 50;

export const getSubtitle = (item: NormalizedImportItem): string | undefined => {
  const totalImported = item.filesImportedCount + item.foldersImported;
  const totalErrors = item.fileImportError + item.folderImportError;
  const totalSkipped =
    item.skippedAlreadyImported + item.skippedExcluded + item.skippedZeroByte + item.skippedUnsupported;
  const totalDiscovered = item.totalFilesFound + item.totalFoldersFound;
  const formattedErrorsCount = `${compactNumber.format(totalErrors)} ${pluralize('error', totalErrors)}`;
  const formattedSkippedCount = `${compactNumber.format(totalSkipped)} ${pluralize('item', totalSkipped)} skipped`;
  // Prefer the specific "already imported" label only when that is the sole skip reason.
  const skippedLabel =
    item.skippedAlreadyImported > 0 && totalSkipped === item.skippedAlreadyImported
      ? `${compactNumber.format(item.skippedAlreadyImported)} skipped (already imported)`
      : formattedSkippedCount;
  const formattedSize = item.importedSize > 0 ? formatBytes(item.importedSize, { numberOfDecimals: 1 }) : null;

  switch (item.status) {
    case 'pending':
      return 'Beginning import...';
    case 'active': {
      if (item.storageExceeded) {
        return 'Stopped';
      }

      if (totalImported > 0 || totalSkipped > 0) {
        // Count skips (and errors) as handled so already-imported backlog doesn't
        // keep the X/Y framing looking stuck.
        const totalHandled = totalImported + totalSkipped + totalErrors;
        const remaining = Math.max(0, totalDiscovered - totalHandled);
        const showProgress = remaining > SHOW_PROGRESS_ITEMS_REMAINING_THRESHOLD;
        let primary: string;

        if (showProgress) {
          primary = `${compactNumber.format(totalHandled)}/${compactNumber.format(totalDiscovered)} ${pluralize('item', totalDiscovered)}`;
        } else if (totalImported > 0) {
          primary = `${compactNumber.format(totalImported)} ${pluralize('item', totalImported)} imported`;
        } else {
          primary = skippedLabel;
        }

        return formatParts(
          primary,
          // When using handled/discovered progress, skips are already in the numerator.
          ...(!showProgress && totalImported > 0 && totalSkipped > 0 ? [skippedLabel] : []),
          ...(totalErrors > 0 ? [formattedErrorsCount] : []),
          ...(formattedSize ? [formattedSize] : []),
        );
      }

      const started = item.startedAt || item.createdAt;

      return started ? `Started ${formatDistanceToNow(parseAirDateToISO(started), { addSuffix: true })}` : 'Started';
    }
    case 'succeeded': {
      const parts: string[] = [];

      if (totalImported > 0) {
        parts.push(`${compactNumber.format(totalImported)} ${pluralize('item', totalImported)}`);
      }
      if (totalSkipped > 0) {
        parts.push(skippedLabel);
      }
      if (totalErrors > 0) {
        parts.push(formattedErrorsCount);
      }
      if (formattedSize) {
        parts.push(formattedSize);
      }
      if (item.updatedAt) {
        parts.push(formatDistanceToNow(parseAirDateToISO(item.updatedAt), { addSuffix: true }));
      }

      return parts.length ? formatParts(...parts) : 'Completed';
    }
    case 'cancelled':
      return item.updatedAt
        ? formatParts('Cancelled', formatDistanceToNow(parseAirDateToISO(item.updatedAt), { addSuffix: true }))
        : 'Cancelled';
    case 'failed':
    case 'exception':
      // Error message rendered instead of subtitle for failed/exception items
      return undefined;
  }
};

export const getImportErrorMessage = (item: NormalizedImportItem) => {
  if (['failed', 'exception'].includes(item.status)) {
    return item.logMessage ?? 'Failed';
  }

  return '';
};

export const convertToFileTrackingItem = ({
  item,
  onRowClick,
  subscription,
  isOutOfStorage,
  includeErrorInSummary,
}: {
  item: NormalizedImportItem;
  onRowClick: (board: LocationBoard) => void;
  subscription?: Subscription;
  isOutOfStorage?: boolean;
  includeErrorInSummary: boolean;
}): FileTrackingItem => {
  const board = getImportDestinationBoard(item);
  const boardName = board.title;
  const title = getImportListItemTitle(item);
  const thumbnail = board.thumbnails?.[0] ?? '';

  return {
    id: item.id,
    status: getImportUploadStatus(item.status, item.storageExceeded),
    error: getImportErrorMessage(item),
    title,
    subtitle: getSubtitle(item),
    sizeInBytes: item.importSizeEstimate,
    currentBytesUploaded: item.importedSize,
    disableCancel: !item.canCancel,
    disableResume: (subscription?.plan === 'basic' || subscription?.pricingModel === 'credit') && isOutOfStorage,
    includeErrorInSummary,
    avatar: thumbnail ? (
      <BoardThumbnail
        className="mr-3 inline-block size-10 shrink-0 rounded-2 object-cover"
        board={{ thumbnails: [thumbnail], title: boardName }}
        width={40}
        height={40}
        sizes="40px"
      />
    ) : (
      <ImportItemIcon source={item.source} />
    ),
    onRowClick: () => onRowClick(board),
  };
};

export const getImportHeaderTitles = ({
  erroredCount,
  pausedCount,
  stoppedCount,
  completedCount,
  abortedCount,
  progressingCount,
}: {
  pausedCount: number;
  stoppedCount: number;
  erroredCount: number;
  completedCount: number;
  abortedCount: number;
  progressingCount: number;
}): { [key in FileStatus]: { title: string; subtitle?: string } } => ({
  [FileStatus.errored]: {
    title: `Import incomplete`,
    subtitle: `${pluralize('Import', erroredCount, true)} failed`,
  },
  [FileStatus.stopped]: {
    title: `${pluralize('Import', stoppedCount, true)} stopped`,
  },
  [FileStatus.paused]: {
    title: `${pluralize('Import', pausedCount, true)} paused`,
  },
  [FileStatus.aborted]: {
    title: `${pluralize('Import', abortedCount, true)} cancelled`,
  },
  [FileStatus.progressing]: {
    title: `${pluralize('Import', progressingCount, true)} in progress`,
  },
  [FileStatus.completed]: {
    title: `${pluralize('Import', completedCount, true)} completed`,
  },
  [FileStatus.preparing]: {
    title: `Preparing`,
    subtitle: `Preparing to import`,
  },
});
